'use client'

import {
  ArrowLeft,
  ChevronRightIcon,
  ChevronUpIcon,
  CreditCard,
  FolderOpenDot,
  GemIcon,
  Gift,
  HelpCircleIcon,
  LogOutIcon,
  Plus,
  Settings,
  Share2,
  Users,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useEffect, useMemo, useState } from 'react'
import { UserHeader } from '@/components/common/user-header'
import { IconBasicHosting, IconPremiumHosting } from '@/components/icons'
import { pushModal } from '@/components/modals'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import { HELP_URL, MIN_COIN_FOR_CODING } from '@/lib/constant'
import { logoutAccount } from '@/lib/db/user'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { SUBSCRIPTION_PRODUCTS } from '@/lib/plans'
import type { User } from '@/lib/types'
import { cn } from '@/lib/utils'
import { ProjectList } from './project-list'

// ListItem component for button actions (modals, toggles, etc.)
const ListItem = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'block select-none leading-none w-full text-left',
      'hover:bg-white/5 transition-colors',
      'flex items-center gap-2 cursor-pointer',
      className,
    )}
    {...props}
  >
    {children}
  </button>
))
ListItem.displayName = 'ListItem'

// LinkItem component for navigation (uses Next.js Link for progress bar support)
const LinkItem = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<typeof Link>
>(({ className, children, ...props }, ref) => (
  <Link
    ref={ref}
    className={cn(
      'block select-none leading-none w-full text-left',
      'hover:bg-white/5 transition-colors',
      'flex items-center gap-2 cursor-pointer',
      className,
    )}
    {...props}
  >
    {children}
  </Link>
))
LinkItem.displayName = 'LinkItem'

interface ISidebarMenuBtn {
  currentProjectId?: string
  user: User | null
  // 是否透明背景
  transparent?: boolean
}

export const SidebarMenuBtn = ({
  currentProjectId,
  user,
  transparent = false,
}: ISidebarMenuBtn) => {
  const [open, setOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isProjectListOpen, setIsProjectListOpen] = useState<boolean>(true)
  const [imageError, setImageError] = useState(false)

  // Reset error state when user or avatar changes
  useEffect(() => {
    setImageError(false)
  }, [user?.avatar])

  const tProfileMenu = useTranslations('profile-menu')
  const router = useRouter()

  const pricingPlanIcon = useMemo(() => {
    if (!user?.subscription?.product_id) {
      return null
    }
    const subscription = SUBSCRIPTION_PRODUCTS.find(
      (product) => product.id === user.subscription?.product_id,
    )
    if (!subscription) {
      return null
    }
    return (
      <img
        src={subscription.icon}
        alt={subscription.title}
        className="size-4"
      />
    )
  }, [user?.subscription?.product_id])

  async function onLogout() {
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_LOGOUT,
      {},
      user?._id.toString(),
    )

    if (isLoggingOut) return

    setIsLoggingOut(true)
    try {
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
      })
    } catch {
      // Fallback to server action for environments where API call fails.
      await logoutAccount()
    } finally {
      setOpen(false)
      router.refresh()
      window.location.assign('/login')
    }
  }

  // Get workspaces for workspace selector
  const { workspaces, defaultWorkspaceId } = useWorkspaces()
  const selectedWorkspaceId = defaultWorkspaceId || null

  // Get current plan from selected workspace
  const currentWorkspace = workspaces.find(
    (w) => w._id.toString() === selectedWorkspaceId,
  )
  const currentPlan = currentWorkspace?.subscription?.plan || 'free'

  // Get plan display info
  const planDisplay = useMemo(() => {
    switch (currentPlan) {
      case 'basic':
        return {
          icon: <IconBasicHosting className="size-4" />,
          label: 'Basic',
        }
      case 'premium':
        return {
          icon: <IconPremiumHosting className="size-4" />,
          label: 'Premium',
        }
      case 'platinum':
        return {
          icon: (
            <Image
              src="/icons/platinum.svg"
              alt="Platinum"
              className="size-4"
              width={16}
              height={16}
            />
          ),
          label: 'Platinum',
        }
      default:
        return {
          icon: null,
          label: 'Free',
        }
    }
  }, [currentPlan])

  if (!user) {
    return null
  }

  return (
    <>
      <UserHeader user={user} onClick={() => setOpen(true)} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className={cn(
            'w-full sm:w-[330px] p-0 text-lg',
            'flex flex-col h-auto max-h-screen',
            transparent ? 'bg-gray-800' : 'bg-gray-900',
            'bg-modal-gradient border-l border-white/10',
            // Override slow 500ms animation to faster 200ms
            'data-[state=open]:duration-200 data-[state=closed]:duration-150',
          )}
        >
          {/* Header Section - User Info + Actions */}
          <div className="px-3 sm:px-3 pt-3 pb-2 border-b border-white/10">
            {/* User Info */}
            <div className="w-full flex items-center bg-transparent border-0 rounded-lg transition-colors px-2 py-1.5 -mx-2 -my-1.5">
              <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-[10px] font-medium uppercase overflow-hidden relative shrink-0">
                {user.avatar && !imageError ? (
                  <img
                    src={user.avatar}
                    alt={user.name || user.email}
                    className="w-full h-full object-cover"
                    onError={() => {
                      setImageError(true)
                    }}
                  />
                ) : (
                  user.email.slice(0, 2).toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-0.5 ml-2">
                <span className="text-xs font-medium text-white truncate text-left">
                  {user.email}
                </span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="flex items-center gap-1 text-[10px] text-white/60">
                    {pricingPlanIcon}
                    <span>
                      {user.coin < MIN_COIN_FOR_CODING
                        ? tProfileMenu('out_of_credit')
                        : `${user.coin?.toFixed(2) || '0.00'} ${tProfileMenu('credits')}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-white/50">
                    {planDisplay.icon}
                    <span>{planDisplay.label}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Back to Ideas + New Project - Inline */}
            <div className="flex items-center gap-1.5 mt-2">
              {/* Back to Ideas Button */}
              <Link
                href="/"
                className={cn(
                  'h-7 px-2.5 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30 rounded-md text-white transition-all duration-200 flex items-center justify-center gap-1.5 shrink-0 flex-1',
                )}
                title="Go back to select Clone, Google Map, or Redesign"
              >
                <ArrowLeft className="size-3.5" />
                <span className="text-[11px] font-medium">
                  {tProfileMenu('back_to_ideas')}
                </span>
              </Link>

              {/* New Project Button - Compact Icon Button */}
              <button
                type="button"
                onClick={() => {
                  window.open('/', '_blank')
                }}
                className={cn(
                  'h-7 px-2.5 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30 rounded-md text-white transition-all duration-200 flex items-center justify-center gap-1.5 shrink-0',
                )}
                title="Open new project in new tab"
              >
                <Plus className="size-3.5" />
              </button>
            </div>
          </div>

          {/* Navigation - Compact */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-3 sm:px-3 py-2 space-y-1">
              <nav className="space-y-1">
                <ListItem
                  onClick={() => {
                    setOpen(false)
                    pushModal('PricingModal', {
                      trigger: 'profile-menu',
                    })
                  }}
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <GemIcon className="size-4 shrink-0" />
                  <span className="text-sm font-medium">
                    {tProfileMenu('pricing')}
                  </span>
                </ListItem>

                {/* Gift Card menu item - temporarily hidden */}
                {/* <ListItem
                  onClick={() => {
                    setOpen(false)
                    window.open('/gift-card', '_blank')
                    sendToBothAnalytics(
                      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_GIFT_CARD,
                      { project_id: currentProjectId },
                      user?._id.toString() || '',
                    )
                  }}
                  className="py-2.5 px-3 sm:px-3 rounded-md relative group"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <Gift className="size-4 shrink-0 text-gold" />
                    <span className="text-sm font-medium">
                      {tProfileMenu('gift_cards')}
                    </span>
                    <span className="text-[9px] bg-red-500/90 text-white px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                      🎄 {tProfileMenu('holiday')}
                    </span>
                  </div>
                </ListItem> */}

                <LinkItem
                  href="/subscription"
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <CreditCard className="size-4 shrink-0" />
                  <span className="text-sm font-medium">
                    {tProfileMenu('subscription')}
                  </span>
                </LinkItem>
                <LinkItem
                  href="/workspaces"
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    <Share2 className="size-4 shrink-0" />
                    <span className="text-sm font-medium truncate">
                      {tProfileMenu('workspaces')}
                    </span>
                  </div>
                </LinkItem>

                <ListItem
                  onClick={() => setIsProjectListOpen(!isProjectListOpen)}
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    <FolderOpenDot className="size-4 shrink-0" />
                    <span className="text-sm font-medium truncate">
                      {tProfileMenu('my-projects')}
                    </span>
                  </div>
                  <ChevronUpIcon
                    className={cn(
                      'size-4 text-white/40 transition-transform shrink-0',
                      {
                        'rotate-180': !isProjectListOpen,
                      },
                    )}
                  />
                </ListItem>
                {isProjectListOpen && (
                  <ProjectList
                    maxColumns={1}
                    currentProjectId={currentProjectId}
                    showSkeletonHeader={false}
                    linkToNewTab={true}
                    cardClassName="hover:border hover:shadow-none hover:border-[rgba(255,255,255,0.2)]"
                    filter="owned"
                  />
                )}

                <div className="bg-white/5 h-px my-1.5" />

                <ListItem
                  onClick={() => {
                    pushModal('SettingModal')
                    sendToBothAnalytics(
                      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_SETTINGS,
                      { project_id: currentProjectId },
                      user?._id.toString() || '',
                    )
                  }}
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <Settings className="size-3.5" />
                  <span className="text-sm font-medium">
                    {tProfileMenu('account_settings')}
                  </span>
                </ListItem>

                <ListItem
                  onClick={() => {
                    const wsId = selectedWorkspaceId || defaultWorkspaceId
                    if (!wsId) {
                      // If no workspace selected, try to open with default workspace or show error
                      if (defaultWorkspaceId) {
                        const ws = workspaces.find(
                          (w) => w._id.toString() === defaultWorkspaceId,
                        )
                        pushModal('WorkspaceSettingsModal', {
                          workspaceId: defaultWorkspaceId,
                          workspaceName: ws?.name || 'Workspace',
                          defaultTab: 'members',
                        })
                      } else if (workspaces.length > 0) {
                        // Use first available workspace
                        const ws = workspaces[0]
                        pushModal('WorkspaceSettingsModal', {
                          workspaceId: ws._id.toString(),
                          workspaceName: ws?.name || 'Workspace',
                          defaultTab: 'members',
                        })
                      }
                      return
                    }
                    const ws = workspaces.find((w) => w._id.toString() === wsId)
                    pushModal('WorkspaceSettingsModal', {
                      workspaceId: wsId,
                      workspaceName: ws?.name || 'Workspace',
                      defaultTab: 'members',
                    })
                  }}
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <Users className="size-3.5" />
                  <span className="text-sm font-medium">
                    {tProfileMenu('team')}
                  </span>
                </ListItem>

                <ListItem
                  onClick={() => {
                    window.open(HELP_URL, '_blank')
                    sendToBothAnalytics(
                      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_HELP_CENTER,
                      { project_id: currentProjectId },
                      user?._id.toString() || '',
                    )
                  }}
                  className="py-2.5 px-3 sm:px-3 rounded-md"
                >
                  <HelpCircleIcon className="size-3.5" />
                  <span className="text-sm font-medium">
                    {tProfileMenu('help-center')}
                  </span>
                </ListItem>

                <div className="bg-white/5 h-px my-1.5" />

                <ListItem
                  onClick={onLogout}
                  disabled={isLoggingOut}
                  className={cn(
                    'py-2.5 px-3 rounded-md',
                    isLoggingOut && 'opacity-60 cursor-not-allowed',
                  )}
                >
                  <LogOutIcon className="size-4" />
                  <span className="text-sm font-medium">
                    {isLoggingOut
                      ? `${tProfileMenu('logout')}...`
                      : tProfileMenu('logout')}
                  </span>
                </ListItem>
              </nav>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
